import { VStack, Box } from "@chakra-ui/react";
import { useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import SelectInput from "../../components/forms/SelectInput";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import FooterPmsSearch from "../../navigation/footer/FooterPmsSearch";
import SearchFormHeader from "../../practitioner-search/SearchFormHeader";
import {
  incrementPmsSearchStep,
  decrementPmsSearchStep,
} from "../../redux/actions/layout";
import { updatePmsSearchDetails } from "../../redux/actions/pmsSearch";
import Lozenge from "../../components/common/Lozenge";
import TextInput from "../../components/forms/TextInput";
import PMSNavButtons from "./PMSNavButtons";
import { useUtilityData } from "../../utilities/contexts";

const allPractitionerTypes = [
  { value: 1, label: "Therapist" },
  { value: 2, label: "Coach" },
  { value: 3, label: "Don't mind", selected: true },
];

const allWomenOrMen = [
  { value: 3, label: "I don't mind", selected: true },
  { value: 1, label: "Female" },
  { value: 2, label: "Male" },
];

const PmsSearchPreferencesForm = (props) => {
  const pmsSearch = useSelector((state) => state.pmsSearch);
  const utilityData = useUtilityData((utilityData) => utilityData);
  const [lgbtq, setLGBTQ] = useState(
    pmsSearch.lgbtq || [
      { value: 1, label: "Yes" },
      { value: 2, label: "No" },
    ]
  );
  const dispatch = useDispatch();
  const formPreferences = useRef();

  //set local state
  const [direction, setDirection] = useState(null);

  const [practitionerTypes, setPractitionerTypes] = useState([
    { value: 1, label: "Therapist" },
    { value: 2, label: "Coach" },
    { value: 3, label: "Don't mind", selected: true },
  ]);
  const [womanOrMan, setWomanOrMan] = useState([
    { value: 3, label: "I don't mind", selected: true },
    { value: 1, label: "Female" },
    { value: 2, label: "Male" },
  ]);
  const [languages, setLanguages] = useState([]);
  const [religions, setReligions] = useState([]);

  const [loaded, setLoaded] = useState(false);
  const { languages: allLanguages, religions: allReligions } = props.utilData;

  useEffect(() => {
    // initalise form
    // pms Search will have full arrays
    pmsSearch.practitionerTypes?.length > 0
      ? setPractitionerTypes(pmsSearch.practitionerTypes)
      : setPractitionerTypes(allPractitionerTypes);
    pmsSearch.languages?.length > 0
      ? setLanguages(pmsSearch.languages)
      : setLanguages(allLanguages);
    pmsSearch.religions?.length > 0
      ? setReligions(pmsSearch.religions)
      : setReligions(allReligions);
    pmsSearch.womanOrMan?.length > 0
      ? setWomanOrMan(pmsSearch.womanOrMan)
      : setWomanOrMan(allWomenOrMen);
    setLoaded(true);
  }, [allLanguages, allReligions, pmsSearch]);

  // Render helpers
  const updateSelectedValue = (val, arr, setArr) => {
    let arrNew = arr.map((elem) =>
      Number(elem.value) === Number(val)
        ? { ...elem, selected: true }
        : { ...elem, selected: false }
    );
    setArr(arrNew);
  };

  // Footer navigation
  const handleClickPrev = () => {
    // Update state by forcing form submit
    setDirection("prev");
    formPreferences.current.handleSubmit();
  };

  const handleClickNext = () => {
    setDirection("next");
    formPreferences.current.handleSubmit();
  };

  // Wait for state to load
  if (!loaded) return null;

  // Render
  return (
    <Formik
      innerRef={formPreferences}
      initialValues={{
        religion: religions,
        culture: "",
        languages: languages,
      }}
      validationSchema={Yup.object({})}
      onSubmit={(values) => {
        values.practitionerTypes = practitionerTypes;
        values.womanOrMan = womanOrMan;
        values.religion = religions;
        values.languages = languages;

        values.lgbtq = lgbtq;

        dispatch(updatePmsSearchDetails(values));
        direction === "next"
          ? dispatch(incrementPmsSearchStep())
          : dispatch(decrementPmsSearchStep());
      }}
    >
      {(formik) => (
        <>
          <Box width="100%" maxW="container.sm" mx="auto" mb="120px" pt="24px">
            <SearchFormHeader />
            <VStack
              as="form"
              maxW="full"
              py={7}
              px={5}
              //mx="auto"
              spacing={8}
              alignItems="flex-start"
              bg="white"
              borderRadius={{ base: 0, md: 20 }}
              onSubmit={formik.handleSubmit}
              boxShadow="lg"
              overflow="hidden"
            >
              <Lozenge
                title="Would you prefer to see a practitioner who identifies as a particular gender?"
                data={womanOrMan}
                onclick={setWomanOrMan}
                required={true}
              />
              <Lozenge
                title="Would you like to see a LGBTQ+ affirmative practitioner?"
                data={lgbtq}
                onclick={setLGBTQ}
                nullOption={{
                  label: "No preference",
                  icon: null,
                }}
                required={true}
              />
              <SelectInput
                name="religion"
                formlabel="Would you prefer to see a practitioner from a particular religious background?"
                variant="filled"
                placeholder="I don't mind"
                value={
                  religions
                    .filter((religion) => religion.selected)
                    .map((elem) => elem.value)[0]
                }
                onChange={(e) =>
                  updateSelectedValue(e.target.value, religions, setReligions)
                }
                required={true}
              >
                {religions.map((item, idx) => (
                  <option value={item.value} key={idx}>
                    {item.label}
                  </option>
                ))}
              </SelectInput>

              <TextInput
                name="culture"
                formlabel="Would you prefer to see a practitioner from a particular culture?"
                type="text"
                placeholder="Please let us know any cultural preferences."
              />

              <SelectInput
                name="languages"
                formlabel="Would your prefer to see a practitioner who speaks another language in addition to English?"
                variant="filled"
                placeholder="I don't mind"
                value={
                  languages
                    .filter((language) => language.selected)
                    .map((elem) => elem.value)[0]
                }
                onChange={(e) =>
                  updateSelectedValue(e.target.value, languages, setLanguages)
                }
                required={true}
              >
                {languages.map((item, idx) => (
                  <option value={item.value} key={idx}>
                    {item.label}
                  </option>
                ))}
              </SelectInput>

              <Box h="24px" w="100%"></Box>
              <PMSNavButtons
                handleClickNext={handleClickNext}
                handleClickPrev={handleClickPrev}
              />
              <FooterPmsSearch
                handleClickNext={handleClickNext}
                handleClickPrev={handleClickPrev}
              />
            </VStack>
          </Box>
          {/* Footer */}
        </>
      )}
    </Formik>
  );
};

export default PmsSearchPreferencesForm;
